'use client';

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAlertSettings, saveAlertSettings } from '../../redux/actions/alertSettingsActions';

const ALERT_DEFINITIONS = [
  {
    category: 'Soumissions',
    alerts: [
      {
        key: 'new_submission',
        label: 'Nouvelle soumission',
        description: 'Email envoyé aux administrateurs lorsqu\'un utilisateur crée une nouvelle soumission.',
        hasStatuses: false
      },
      {
        key: 'submission_status',
        label: 'Changement de statut de soumission',
        description: 'Email envoyé à l\'utilisateur lorsque le statut de sa soumission est modifié.',
        hasStatuses: true,
        statuses: [
          { value: 'pending', label: 'En attente' },
          { value: 'approved', label: 'Approuvée' },
          { value: 'rejected', label: 'Rejetée' },
          { value: 'in_progress', label: 'En cours de traitement' }
        ]
      }
    ]
  },
  {
    category: 'Commandes',
    alerts: [
      {
        key: 'order_status',
        label: 'Changement de statut de commande',
        description: 'Email envoyé à l\'utilisateur lorsque le statut de sa commande est modifié.',
        hasStatuses: true,
        statuses: [
          { value: 'pending', label: 'En attente' },
          { value: 'accepted', label: 'Acceptée' },
          { value: 'in_progress', label: 'En cours de traitement' },
          { value: 'completed', label: 'Terminée' },
          { value: 'sent', label: 'Expédiée' },
          { value: 'cancelled', label: 'Annulée' }
        ]
      }
    ]
  },
  {
    category: 'Authentification',
    alerts: [
      {
        key: 'email_verification',
        label: 'Vérification d\'email',
        description: 'Email de confirmation envoyé à l\'utilisateur lors de son inscription.',
        hasStatuses: false
      },
      {
        key: 'welcome',
        label: 'Email de bienvenue',
        description: 'Email de bienvenue envoyé après création du compte.',
        hasStatuses: false
      },
      {
        key: 'password_reset',
        label: 'Réinitialisation de mot de passe',
        description: 'Email envoyé lors d\'une demande de réinitialisation de mot de passe.',
        hasStatuses: false
      },
      {
        key: 'invitation',
        label: 'Invitation utilisateur',
        description: 'Email d\'invitation envoyé lorsqu\'un administrateur invite un nouvel utilisateur.',
        hasStatuses: false
      }
    ]
  }
];

const AlertSettings = () => {
  const dispatch = useDispatch();
  const { settings, loading, saving, error } = useSelector(state => state.alertSettings);
  const [localSettings, setLocalSettings] = useState({});
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    dispatch(fetchAlertSettings());
  }, [dispatch]);

  // Initialise l'état local dès que les settings sont chargés depuis l'API
  useEffect(() => {
    if (settings.length > 0) {
      const map = {};
      settings.forEach(s => {
        map[s.alert_key] = {
          enabled: s.enabled,
          config: s.config || {}
        };
      });
      setLocalSettings(map);
    }
  }, [settings]);

  const getSetting = (key) => localSettings[key] || { enabled: true, config: {} };

  const toggleEnabled = (key) => {
    setLocalSettings(prev => ({
      ...prev,
      [key]: { ...getSetting(key), enabled: !getSetting(key).enabled }
    }));
    setSaveSuccess(false);
  };

  const toggleStatus = (key, status) => {
    const current = getSetting(key);
    const statuses = current.config.statuses || [];
    const next = statuses.includes(status)
      ? statuses.filter(s => s !== status)
      : [...statuses, status];
    setLocalSettings(prev => ({
      ...prev,
      [key]: { ...current, config: { ...current.config, statuses: next } }
    }));
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    const payload = Object.entries(localSettings).map(([alert_key, val]) => ({
      alert_key,
      enabled: val.enabled,
      config: val.config
    }));
    await dispatch(saveAlertSettings(payload));
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-state">Chargement des paramètres...</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Alertes email</h1>
        <p className="page-subtitle">Configurez les alertes email envoyées automatiquement par la plateforme.</p>
      </div>

      {error && (
        <div className="alert alert-danger">{error}</div>
      )}

      {saveSuccess && (
        <div className="alert alert-success">Paramètres sauvegardés avec succès.</div>
      )}

      <div className="alert-settings-list">
        {ALERT_DEFINITIONS.map(group => (
          <div key={group.category} className="card alert-settings-group">
            <div className="card-header">
              <h2 className="alert-settings-category">{group.category}</h2>
            </div>
            <div className="card-body">
              {group.alerts.map(def => {
                const setting = getSetting(def.key);
                return (
                  <div key={def.key} className={`alert-setting-row ${!setting.enabled ? 'alert-setting-disabled' : ''}`}>
                    <div className="alert-setting-header">
                      <div className="alert-setting-info">
                        <span className="alert-setting-label">{def.label}</span>
                        <span className="alert-setting-description">{def.description}</span>
                      </div>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={setting.enabled}
                          onChange={() => toggleEnabled(def.key)}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>

                    {def.hasStatuses && setting.enabled && (
                      <div className="alert-setting-statuses">
                        <span className="alert-statuses-label">Déclencher pour les statuts :</span>
                        <div className="alert-statuses-grid">
                          {def.statuses.map(s => {
                            const active = (setting.config.statuses || []).includes(s.value);
                            return (
                              <label key={s.value} className="status-checkbox">
                                <input
                                  type="checkbox"
                                  checked={active}
                                  onChange={() => toggleStatus(def.key, s.value)}
                                />
                                <span className={`status-badge status-${s.value}`}>{s.label}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="alert-settings-actions">
        <button
          className="btn btn-primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Sauvegarde...' : 'Sauvegarder les paramètres'}
        </button>
      </div>
    </div>
  );
};

export default AlertSettings;
